import { config } from '@/src/config/app-config';
import { useCampaigns } from '@/src/store/campaigns.context';
import { useFlashbars } from '@/src/store/flashbar.context';
import { useJamChallenge } from '@/src/store/jam-challenge.context';
import { CampaignAttemptResponse } from '@/src/types/Campaign';
import { JamEventDetails } from '@/src/types/JamEventDetails';
import { getErrorMessage } from '@/src/utils/errors.utils';
import { isDateInThePast } from '@/src/utils/event-campaign-common.utils';
import { i18nKeys } from '@/src/utils/i18n.utils';
import { Box, Button, Container, Header } from '@amzn/awsui-components-react';
import moment from 'moment';
import converter from 'number-to-words';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './JamCampaignRules.module.scss';

interface JamCampaignRulesProps {
  event?: JamEventDetails;
  campaignAttempt?: CampaignAttemptResponse;
  handleCloseRules: () => void;
}

const JamCampaignRules: React.FC<JamCampaignRulesProps> = ({ event, campaignAttempt, handleCloseRules }) => {
  const { t } = useTranslation();
  const {
    startCampaignAttempt,
    startCampaignAttemptWithRegistrationId,
    handleIsFetchingCampaign,
    getCampaignEventDetails,
    handleShowCampaignRules,
  } = useCampaigns();
  const { loadJamChallengeData } = useJamChallenge();
  const { addErrorFlashbar } = useFlashbars();

  // Handle adding to the page and removing from the page
  const handleClick = async () => {
    // Start campaign attempt 1
    try {
      if (event?.eventName && !event.ended && (!campaignAttempt || campaignAttempt?.attemptNumber === 0)) {
        handleIsFetchingCampaign(true);
        // Start a campaign attempt
        if (config.isLrsIntegrationEnabled === true) {
          const params = new URLSearchParams(location.search);
          const registrationId = params.get('registration_id');

          if (registrationId !== null) {
            await startCampaignAttemptWithRegistrationId(event?.eventName, registrationId, true);
          } else {
            await startCampaignAttempt(event?.eventName, true);
          }
        } else {
          await startCampaignAttempt(event?.eventName, true);
        }
        // Fetch the campaign attempt
        await getCampaignEventDetails(event?.eventName, true, true);
        // Fetch challenges
        await loadJamChallengeData({ id: event?.eventName, showLoader: true });
      } else {
        // Close rules page
        handleCloseRules();
      }
    } catch (err) {
      addErrorFlashbar(getErrorMessage(err));
    } finally {
      handleIsFetchingCampaign(false);
    }
  };

  // Event end date and time
  const endDateTime = event?.eventEndDate;

  // Event challenge count
  const challengeCount = event?.challenges.length || 0;

  // Campaign attempt duration
  const sessionDurationLimitHours = campaignAttempt?.sessionDurationLimitHours || 0;

  // Challenge lab duration
  const labTimeoutHours = campaignAttempt?.labTimeoutHours || 0;

  // Number of allowed campaign attempts
  const allowedAttempts = campaignAttempt?.allowedAttempts || 0;

  const passingScore = campaignAttempt?.passingScore as number;

  const totalScore = event?.challenges
    ?.map((challenge) => challenge.maxPoints)
    .reduce((accum, curr) => accum + curr, 0);

  // Event start date and time
  const eventStartDate = event?.eventStartDate || '';

  // Split dates into dates and times
  const endTime = moment(endDateTime).format('LT');
  const endDate = moment(endDateTime).format('LL');
  const startTime = moment(eventStartDate).format('LT');
  const startDate = moment(eventStartDate).format('LL');

  // Convert values to a word representation. I.E 1 becomes one.
  const parsedChallengeCount = converter.toWords(challengeCount);
  const parsedSessionDurationLimitHours = converter.toWords(sessionDurationLimitHours);
  const parsedLabTimeoutHours = converter.toWords(labTimeoutHours);
  const parsedAllowedAttempts = converter.toWords(allowedAttempts);

  // Determine if the event has started
  const eventStarted = isDateInThePast(eventStartDate);

  // Whenever this component unmounts we need
  // to make sure that this value gets reset to defaul
  useEffect(() => {
    return () => {
      handleShowCampaignRules(false);
    };
  }, []);

  return (
    <div className={styles.rulesComponent}>
      <div className={styles.rulesImageContainer}>
        <img src="/assets/WavingHandMonkey.png" className={styles.rulesImage} alt="" />
      </div>
      <Container className={styles.rulesContainer}>
        <Header
          variant="h1"
          actions={
            eventStarted ? (
              <Button
                variant="icon"
                iconName="close"
                onClick={() => void handleClick()}
                data-testid="campaignRulesCloseButton"
                ariaLabel={t(i18nKeys.campaigns.rules.close)}
              />
            ) : null
          }
        >
          <p className={styles.rulesHeader}>{t(i18nKeys.campaigns.rules.title)}</p>
        </Header>
        <div className={styles.rule}>
          <Box margin={{ top: 's', bottom: 'l' }}>
            <p className={styles.rulesDescription}>{t(i18nKeys.campaigns.rules.description)}</p>
          </Box>
        </div>
        <div className={styles.rule}>
          <h2 className={styles.rulesSubHeader}>{t(i18nKeys.campaigns.rules.deadline.title)}</h2>
          <Box margin={{ top: 'xs', bottom: 'l' }}>
            <p className={styles.rulesDescription}>
              {t(i18nKeys.campaigns.rules.deadline.description, { date: endDate, time: endTime })}
            </p>
          </Box>
        </div>
        <div className={styles.rule}>
          <h2 className={styles.rulesSubHeader}>{t(i18nKeys.campaigns.rules.challenges.title)}</h2>
          <Box margin={{ top: 'xs', bottom: 'l' }}>
            <p className={styles.rulesDescription}>
              {t(
                challengeCount > 1
                  ? i18nKeys.campaigns.rules.challenges.description_plural
                  : i18nKeys.campaigns.rules.challenges.description_singular,
                { challengeCount: parsedChallengeCount }
              )}
            </p>
          </Box>
        </div>
        <div className={styles.rule}>
          <h2 className={styles.rulesSubHeader}>{t(i18nKeys.campaigns.rules.timeConstraints.title)}</h2>
          <Box margin={{ top: 'xs', bottom: 'l' }}>
            <p className={styles.rulesDescription}>
              {t(
                sessionDurationLimitHours > 1
                  ? i18nKeys.campaigns.rules.timeConstraints.descriptionHours_plural
                  : i18nKeys.campaigns.rules.timeConstraints.descriptionHours_singular,
                {
                  hours: parsedSessionDurationLimitHours,
                }
              ) +
                ' ' +
                t(
                  challengeCount > 1
                    ? i18nKeys.campaigns.rules.timeConstraints.descriptionChallengeCount_plural
                    : i18nKeys.campaigns.rules.timeConstraints.descriptionChallengeCount_singular,
                  {
                    challengeCount: parsedChallengeCount,
                  }
                ) +
                ' ' +
                t(
                  labTimeoutHours > 1
                    ? i18nKeys.campaigns.rules.timeConstraints.descriptionLabDuration_plural
                    : i18nKeys.campaigns.rules.timeConstraints.descriptionLabDuration_singular,
                  {
                    labDuration: parsedLabTimeoutHours,
                  }
                )}
            </p>
          </Box>
        </div>
        {passingScore && totalScore && (
          <div className={styles.rule}>
            <h2 className={styles.rulesSubHeader}>{t(i18nKeys.campaigns.rules.passingScore.title)}</h2>
            <Box margin={{ top: 'xs', bottom: 'l' }}>
              <p className={styles.rulesDescription}>
                {t(i18nKeys.campaigns.rules.passingScore.descriptionPassingScore, {
                  passingScore,
                  totalScore,
                })}
              </p>
            </Box>
          </div>
        )}
        <div className={styles.rule}>
          <h2 className={styles.rulesSubHeader}>{t(i18nKeys.campaigns.rules.numberOfAttempts.title)}</h2>
          <Box margin={{ top: 'xs', bottom: 'l' }}>
            <p className={styles.rulesDescription}>
              {t(
                allowedAttempts > 1
                  ? i18nKeys.campaigns.rules.numberOfAttempts.descriptionAllowedAttempts_plural
                  : i18nKeys.campaigns.rules.numberOfAttempts.descriptionAllowedAttempts_singular,
                {
                  allowedAttempts: parsedAllowedAttempts,
                }
              ) +
                ' ' +
                t(
                  sessionDurationLimitHours > 1
                    ? i18nKeys.campaigns.rules.numberOfAttempts.descriptionAttemptDuration_plural
                    : i18nKeys.campaigns.rules.numberOfAttempts.descriptionAttemptDuration_singular,
                  {
                    attemptDuration: parsedSessionDurationLimitHours,
                  }
                )}
            </p>
          </Box>
        </div>
        <div className={styles.rule}>
          <h2 className={styles.rulesSubHeader}>{t(i18nKeys.campaigns.rules.readyToStart.title)}</h2>
          <Box margin={{ top: 'xs', bottom: 'xxl' }}>
            <p className={styles.rulesDescription}>{t(i18nKeys.campaigns.rules.readyToStart.description)}</p>
          </Box>
        </div>
        <div className={styles.rulesFooter}>
          {!eventStarted ? (
            <p className={styles.rulesCampaignNotStart}>
              {t(i18nKeys.campaigns.rules.notStarted.description, { date: startDate, time: startTime })}
            </p>
          ) : (
            <Button onClick={() => void handleClick()} variant="primary" data-testid="campaignRulesBeginButton">
              {t(i18nKeys.campaigns.rules.begin)}
            </Button>
          )}
        </div>
      </Container>
    </div>
  );
};

export default JamCampaignRules;
